import React, { useState } from 'react';
import { formatDateTimeMEL, formatUtcToIstForHistoryView } from 'src/helpers/common_helpers';
import './TicketHistoryLog.scss';

export interface TicketLogEntry {
    enquiryId: string;
    action: string;
    sequenceNumber: number;
    category: string;
    subject: string;
    description: string;
    priority: string;
    status: string;
    supporterId: string;
    userId: string;
    customerName: string;
    supporterName: string | null;
    parentId: string | null;
    createdByName: string;
    isDeleted: boolean;
    deleterId: string | null;
    deletionTime: string | null;
    lastModificationTime: string | null;
    lastModifierId: string | null;
    creationTime: string;
    creatorId: string;
    id: string;
}

interface TicketHistoryLogProps {
    ticketLog: TicketLogEntry[];
    title?: string;
}

interface FieldChange {
    field: string;
    value: string;
    displayValue?: string;
}

const getActionIcon = (action: string): string => {
    switch (action?.toLowerCase()) {
        case 'new':
            return 'fas fa-plus-circle';
        case 'reassign':
            return 'fas fa-user-edit';
        case 'closed':
        case 'close':
            return 'fas fa-check-circle';
        case 'reopen':
            return 'fas fa-redo';
        case 'resolve':
            return 'fas fa-clipboard-check';
        default:
            return 'fas fa-info-circle';
    }
};

const getActionColor = (action: string): string => {
    switch (action?.toLowerCase()) {
        case 'new':
            return '#4caf50';
        case 'reassign':
            return '#ff9800';
        case 'closed':
        case 'close':
            return '#2196f3';
        case 'reopen':
            return '#9c27b0';
        case 'resolve':
            return '#009688';
        default:
            return '#607d8b';
    }
};

const getPriorityBadgeClass = (priority: string): string => {
    switch (priority?.toLowerCase()) {
        case 'high':
            return 'priority-high';
        case 'medium':
            return 'priority-medium';
        case 'low':
            return 'priority-low';
        default:
            return 'priority-default';
    }
};

const getStatusBadgeClass = (status: string): string => {
    switch (status?.toLowerCase()) {
        case 'new':
            return 'status-new';
        case 'reassign':
            return 'status-reassign';
        case 'closed':
        case 'close':
            return 'status-closed';
        case 'reopen':
            return 'status-reopen';
        case 'resolve':
            return 'status-resolve';
        default:
            return 'status-default';
    }
};

const TicketHistoryLog: React.FC<TicketHistoryLogProps> = ({ ticketLog, title = 'History' }) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    if (!ticketLog || ticketLog.length === 0) {
        return null;
    }

    // Sort by creation time (newest first for display, but we'll reverse for timeline)
    const sortedLog = [...ticketLog].sort(
        (a, b) => new Date(a.creationTime).getTime() - new Date(b.creationTime).getTime()
    );

    const toggleItemExpand = (id: string) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const getFieldChanges = (entry: TicketLogEntry): FieldChange[] => {
        const changes: FieldChange[] = [];

        if (entry.category) {
            changes.push({ field: 'Category', value: entry.category });
        }
        if (entry.subject) {
            changes.push({ field: 'Subject', value: entry.subject });
        }
        if (entry.priority) {
            changes.push({
                field: 'Priority',
                value: entry.priority,
                displayValue: entry.priority.charAt(0).toUpperCase() + entry.priority.slice(1),
            });
        }
        if (entry.status) {
            changes.push({ field: 'Status', value: entry.status });
        }
        if (entry.supporterName) {
            changes.push({ field: 'Assigned To', value: entry.supporterName });
        }
        if (entry.customerName) {
            changes.push({ field: 'Customer', value: entry.customerName });
        }

        return changes;
    };

    const getActionDescription = (entry: TicketLogEntry): string => {
        switch (entry.action?.toLowerCase()) {
            case 'new':
                return 'created the ticket';
            case 'reassign':
                return `reassigned the ticket${entry.supporterName ? ` to ${entry.supporterName}` : ''}`;
            case 'closed':
            case 'close':
                return 'closed the ticket';
            case 'reopen':
                return 'reopened the ticket';
            case 'resolve':
                return 'resolved the ticket';
            default:
                return `updated the ticket (${entry.action})`;
        }
    };

    return (
        <div className="ticket-history-log">
            <div className="history-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="header-left">
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} toggle-icon`}></i>
                    <span className="history-title">{title}</span>
                    <span className="history-count">{ticketLog.length}</span>
                </div>
            </div>

            {isExpanded && (
                <div className="history-timeline">
                    {sortedLog.map((entry, index) => {
                        const isItemExpanded = expandedItems.has(entry.id);
                        const fieldChanges = getFieldChanges(entry);
                        const actionColor = getActionColor(entry.action);

                        return (
                            <div key={entry.id} className="timeline-item">
                                <div className="timeline-connector">
                                    <div
                                        className="timeline-dot"
                                        style={{ backgroundColor: actionColor }}
                                    >
                                        <i className={getActionIcon(entry?.action)}></i>
                                    </div>
                                    {index < sortedLog.length - 1 && <div className="timeline-line"></div>}
                                </div>

                                <div className="timeline-content">
                                    <div
                                        className="timeline-card"
                                        onClick={() => toggleItemExpand(entry?.id)}
                                    >
                                        <div className="card-header">
                                            <div className="header-info">
                                                <span className="user-avatar">
                                                    {(entry?.customerName || 'U').charAt(0).toUpperCase()}
                                                </span>
                                                <div className="user-details">
                                                    <span className="user-name">{entry?.customerName || 'Unknown User'}</span>
                                                    <span className="action-text">{getActionDescription(entry)}</span>
                                                </div>
                                            </div>
                                            <div className="header-meta">
                                                <span className="timestamp">
                                                    {formatUtcToIstForHistoryView(entry?.creationTime, true)}
                                                </span>
                                                <i
                                                    className={`fas fa-chevron-${isItemExpanded ? 'up' : 'down'} expand-icon`}
                                                ></i>
                                            </div>
                                        </div>

                                        {isItemExpanded && fieldChanges.length > 0 && (
                                            <div className="card-details">
                                                <div className="details-grid">
                                                    {fieldChanges.map((change, i) => (
                                                        <div key={i} className="detail-row">
                                                            <span className="detail-label">{change.field}</span>
                                                            <span className="detail-value">
                                                                {change.field === 'Priority' ? (
                                                                    <span className={`badge ${getPriorityBadgeClass(change.value)}`}>
                                                                        {change.displayValue || change.value}
                                                                    </span>
                                                                ) : change.field === 'Status' ? (
                                                                    <span className={`badge ${getStatusBadgeClass(change.value)}`}>
                                                                        {change.value}
                                                                    </span>
                                                                ) : (
                                                                    change.displayValue || change.value
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TicketHistoryLog;
